import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Sparkles, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ThemeToggle = () => {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <motion.button
      onClick={toggleTheme}
      className="relative w-14 h-7 rounded-full p-0.5 transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-1 dark:focus:ring-offset-gray-800 overflow-hidden"
      style={{
        background: isDarkMode 
          ? 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)'
          : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fcd34d 100%)'
      }}
      whileHover={{ 
        scale: 1.1,
        boxShadow: isDarkMode 
          ? '0 0 30px rgba(99, 102, 241, 0.4)' 
          : '0 0 30px rgba(251, 191, 36, 0.4)'
      }}
      whileTap={{ scale: 0.95 }}
      aria-label="Toggle theme"
    >
      {/* Animated background particles */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          background: isDarkMode 
            ? 'radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 20%, rgba(251, 191, 36, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)'
        }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
      />

      {/* Floating sparkles */}
      {!isDarkMode && (
        <>
          <motion.div
            className="absolute top-0.5 left-1 w-0.5 h-0.5 bg-yellow-400 rounded-full"
            animate={{
              y: [0, -2, 0],
              opacity: [0.5, 1, 0.5],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="absolute top-1 right-1.5 w-0.5 h-0.5 bg-orange-400 rounded-full"
            animate={{
              y: [0, -1.5, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [0.6, 1, 0.6]
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
          />
          <motion.div
            className="absolute bottom-1 left-2 w-0.5 h-0.5 bg-yellow-300 rounded-full"
            animate={{
              y: [0, -1, 0],
              opacity: [0.4, 0.9, 0.4],
              scale: [0.7, 1.1, 0.7]
            }}
            transition={{ duration: 1.8, repeat: Infinity, delay: 1 }}
          />
        </>
      )}

      {/* Stars for dark mode */}
      {isDarkMode && (
        <>
          <motion.div
            className="absolute top-0.5 left-1 w-0.5 h-0.5 bg-indigo-300 rounded-full"
            animate={{
              opacity: [0.3, 1, 0.3],
              scale: [0.5, 1, 0.5]
            }}
            transition={{ duration: 3, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="absolute top-1 right-1.5 w-0.5 h-0.5 bg-purple-300 rounded-full"
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [0.4, 0.8, 0.4]
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="absolute bottom-1 left-2 w-0.5 h-0.5 bg-blue-300 rounded-full"
            animate={{
              opacity: [0.4, 0.9, 0.4],
              scale: [0.6, 1, 0.6]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          />
        </>
      )}

      {/* Main toggle handle */}
      <motion.div
        className="relative w-6 h-6 rounded-full shadow-xl flex items-center justify-center overflow-hidden"
        style={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
        }}
        animate={{
          x: isDarkMode ? 28 : 0,
        }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 25
        }}
      >
        {/* Handle glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{
            boxShadow: isDarkMode 
              ? '0 0 20px rgba(99, 102, 241, 0.3), inset 0 0 20px rgba(139, 92, 246, 0.1)'
              : '0 0 20px rgba(251, 191, 36, 0.3), inset 0 0 20px rgba(245, 158, 11, 0.1)'
          }}
          transition={{ duration: 0.5 }}
        />

        {/* Icon container with rotation */}
        <motion.div
          className="relative z-10"
          animate={{
            rotate: isDarkMode ? 180 : 0,
            scale: isDarkMode ? 0.9 : 1.1,
          }}
          transition={{ 
            duration: 0.6,
            type: "spring",
            stiffness: 300
          }}
        >
          {isDarkMode ? (
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <Moon className="w-3 h-3 text-indigo-300" />
            </motion.div>
          ) : (
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <Sun className="w-3 h-3 text-yellow-500" />
            </motion.div>
          )}
        </motion.div>

        {/* Pulse effect */}
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0, 0.3]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            background: isDarkMode 
              ? 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(251, 191, 36, 0.3) 0%, transparent 70%)'
          }}
        />
      </motion.div>

      {/* Background transition effect */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          background: isDarkMode 
            ? 'linear-gradient(45deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
            : 'linear-gradient(45deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)'
        }}
        transition={{ duration: 0.8 }}
      />

      {/* Border glow */}
      <motion.div
        className="absolute inset-0 rounded-full border-2"
        animate={{
          borderColor: isDarkMode 
            ? 'rgba(99, 102, 241, 0.3)' 
            : 'rgba(251, 191, 36, 0.3)'
        }}
        transition={{ duration: 0.5 }}
      />
    </motion.button>
  );
};

export default ThemeToggle;
